import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  useMediaQuery,
  useTheme,
  TextField,
  Divider,
  InputAdornment,
  Tab,
  Tabs,
  IconButton,
  Switch,
  FormControlLabel,
  Paper,
  Collapse,
  Tooltip,
  Popover,
  Link,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CurrencyTextField from '../../components/CurrencyTextField';
import {
  beginProfileSaveBack,
  endProfileSaveBack,
  hasPendingHistoryLoad,
  isProfileSaveBackInProgress,
  useLinkedCalculator,
  type SharedProceedsData,
} from '../../context/LinkedCalculatorContext';
import { useProfile } from '../../context/ProfileContext';
import HistoryToolbar, { type NewProfileDraft } from '../../components/HistoryToolbar';
import TextSizeControl from '../../components/TextSizeControl';
import ExportPdfButton from '../../components/ExportPdfButton';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { logCalculatorInteraction, logPdfExport } from '../../services/analytics';
import { exportSellingToPDF, type SellingScheduleRowPdf } from '../../utils/sellingCalculatorPdfExport';
import { calculateSellerStampDuty } from '../../utils/sellerStampDuty';
import {
  CalculatorTutorial,
  TutorialLaunchButton,
  useCalculatorTutorial,
  type TutorialStepDef,
} from '../../components/CalculatorTutorial';
import WhatsNewDialog, {
  WhatsNewLaunchButton,
  useWhatsNew,
} from '../../components/WhatsNewDialog';
import { SELLING_WHATS_NEW } from '../../utils/sellingWhatsNew';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return `S$${Math.round(n).toLocaleString('en-SG')}`;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addWorkingDays(date: Date, wDays: number): Date {
  let count = 0;
  const d = new Date(date);
  while (count < wDays) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return d;
}

function toDateStr(date: Date): string {
  return date.toISOString().split('T')[0];
}

function parseDate(str: string): Date | null {
  if (!str) return null;
  const d = new Date(str + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function formatDateDisplay(d: Date): string {
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}

function calcSSD(purchaseDateStr: string, saleDateStr: string, salePrice: number): number {
  return calculateSellerStampDuty(purchaseDateStr, saleDateStr, salePrice)?.amount ?? 0;
}

const TODAY_STR = toDateStr(new Date());
const IRAS_SSD_URL = 'https://www.iras.gov.sg/taxes/stamp-duty/for-property/selling-or-disposing-property/seller%27s-stamp-duty-%28ssd%29-for-residential-property?utm_source=chatgpt.com';

// ─── Types ────────────────────────────────────────────────────────────────────

type PropertyTab = 'hdb' | 'private';

/** How much of the sale the form asks for.
 *  - `detailed`: the full picture — two owners, their shares, a CPF figure each.
 *  - `simple`: the same sale with one owner, so the only CPF question is the
 *    single withdrawal-with-accrued-interest figure. A second seller entered in
 *    detailed mode is kept, just ignored, so switching back restores the split. */
type SellingCalculatorMode = 'simple' | 'detailed';

interface SellingInputs {
  salePrice: number;
  outstandingLoan: number;
  /** The owners selling this property. One seller holds 100%; two split the
   *  proceeds by their shares. Each refunds their own CPF out of their own share. */
  sellers: SellerState[];
  legalFee: number;
  agentFee: number;
  /** How the agent fee is entered: a % of the sale price, or a flat amount. */
  agentFeeMode: 'pct' | 'amount';
  /** Percentage of the sale price used when agentFeeMode === 'pct'. */
  agentFeePct: number;
  /** Adds GST on top of the fee when true. */
  agentFeeGstInclusive: boolean;
  bankLoanPenalty: number;
  miscFee: number;
  resaleApplication: number;
  resaleLevy: number;
  hdbUpgradingFee: number;
  purchaseDate: string;
  saleDate: string;
}

interface HdbDates {
  otp: string;
  exercise: string;
  resaleApp: string;
  acceptance: string;
  endorsement: string;
  approval: string;
  completion: string;
  handover: string;
}

interface PrivateDates {
  otp: string;
  exercise: string;
  completion: string;
}

/** One owner on the sale. `sharePct` is their share of the proceeds pool. */
interface SellerState {
  name?: string;
  sharePct: number;
  /** CPF withdrawn for this property plus accrued interest — refunded to their OA. */
  cpfWithdrawal: number;
}

const createSeller = (sharePct: number, name?: string): SellerState => ({
  name,
  sharePct,
  cpfWithdrawal: 0,
});

/** Every seller's CPF refund — the figure the proceeds maths has always used. */
const totalCpfRefund = (sellers: SellerState[]): number =>
  sellers.reduce((sum, seller) => sum + Math.max(0, seller.cpfWithdrawal), 0);

// Every field on a seller card lines up on this width, whatever its label.
const SELLER_FIELD_WIDTH = 190;

// Agent service fee: 2% of the sale price by default, plus GST at the prevailing rate.
const DEFAULT_AGENT_FEE_PCT = 2;
const AGENT_FEE_GST = 1.09;

/** The fee actually charged: the % or flat basis, with GST added when included. */
const computeAgentFee = (i: SellingInputs): number => {
  const base = i.agentFeeMode === 'pct' ? (i.salePrice * (i.agentFeePct || 0)) / 100 : i.agentFee;
  return Math.round(Math.max(0, base) * (i.agentFeeGstInclusive ? AGENT_FEE_GST : 1));
};

/** Restores inputs saved before the fee had a mode / % / GST option. Such an entry
 *  keeps the exact figure it was saved with: a typed amount stays a flat amount and
 *  GST is left off, since none was applied then. */
const normalizeSellingInputs = (raw: unknown, defaults: SellingInputs): SellingInputs => {
  const i = (raw ?? {}) as Partial<SellingInputs> & { cpfWithdrawal?: number };
  // Entries saved before the sale could be split carry one CPF figure for the
  // whole sale — that becomes a single seller holding 100%.
  const sellers = Array.isArray(i.sellers) && i.sellers.length > 0
    ? i.sellers.map((seller, idx) => ({
        name: seller?.name,
        sharePct: typeof seller?.sharePct === 'number' ? seller.sharePct : (idx === 0 ? 100 : 0),
        cpfWithdrawal: Math.max(0, Math.round(seller?.cpfWithdrawal ?? 0)),
      }))
    : [{ ...createSeller(100), cpfWithdrawal: Math.max(0, Math.round(i.cpfWithdrawal ?? 0)) }];

  const agentFee = i.agentFeeMode
    ? {}
    : {
        agentFeeMode: ((i.agentFee ?? 0) > 0 ? 'amount' : 'pct') as 'pct' | 'amount',
        agentFeePct: DEFAULT_AGENT_FEE_PCT,
        agentFeeGstInclusive: false,
      };

  return { ...defaults, ...i, ...agentFee, sellers } as SellingInputs;
};

const DEFAULT_HDB: SellingInputs = {
  salePrice: 0, outstandingLoan: 0, sellers: [createSeller(100)],
  legalFee: 3000, agentFee: 0, bankLoanPenalty: 0,
  agentFeeMode: 'pct', agentFeePct: DEFAULT_AGENT_FEE_PCT, agentFeeGstInclusive: true,
  miscFee: 150, resaleApplication: 80,
  resaleLevy: 0, hdbUpgradingFee: 0,
  purchaseDate: '', saleDate: '',
};

const DEFAULT_PRIVATE: SellingInputs = {
  salePrice: 0, outstandingLoan: 0, sellers: [createSeller(100)],
  legalFee: 3000, agentFee: 0, bankLoanPenalty: 0,
  agentFeeMode: 'pct', agentFeePct: DEFAULT_AGENT_FEE_PCT, agentFeeGstInclusive: true,
  miscFee: 0, resaleApplication: 0,
  resaleLevy: 0, hdbUpgradingFee: 0,
  purchaseDate: '', saleDate: '',
};

// ─── Small reusable components ────────────────────────────────────────────────

// ─── SliderMoneyInput ─────────────────────────────────────────────────────────
// Kept as the existing call-site name, but rendered as a direct currency field.

const SliderMoneyInput: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max: number;
  step: number;
  bigStep?: number;
  color: string;
  isMobile: boolean;
  helperText?: string;
  commitMax?: number;
}> = ({ label, value, onChange, min = 0, color, helperText, commitMax }) => {
  const [display, setDisplay] = useState(() => value === 0 ? '' : Math.round(value).toLocaleString('en-SG'));

  useEffect(() => {
    setDisplay(value === 0 ? '' : Math.round(value).toLocaleString('en-SG'));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    const raw = digits === '' ? 0 : parseInt(digits, 10);
    const next = Math.max(min, Math.min(commitMax ?? Number.MAX_SAFE_INTEGER, Number.isFinite(raw) ? raw : 0));
    setDisplay(digits === '' ? '' : next.toLocaleString('en-SG'));
    onChange(next);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={display}
        placeholder="0"
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        onChange={handleChange}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': { borderColor: color },
          },
          '& input': { fontWeight: 600, textAlign: 'right' },
        }}
      />
      {helperText && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

const DateField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  info?: string;
}> = ({ label, value, onChange, info }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      {info && (
        <Tooltip title={info} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
    <TextField
      size="small"
      fullWidth
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }}
      InputLabelProps={{ shrink: true }}
    />
  </Box>
);

// ─── DayNumberInput ───────────────────────────────────────────────────────────
// Plain integer input rendered with a "days" suffix (not currency).

const DayNumberInput: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  color: string;
  helperText?: string;
}> = ({ label, value, onChange, color, helperText }) => {
  const [display, setDisplay] = useState(() => String(value));

  useEffect(() => {
    setDisplay(String(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    setDisplay(digits);
    onChange(digits === '' ? 0 : parseInt(digits, 10));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={display}
        placeholder="14"
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
        onChange={handleChange}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': { borderColor: color },
          },
          '& input': { fontWeight: 600, textAlign: 'right' },
        }}
      />
      {helperText && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

// ─── Schedule Stage Card ──────────────────────────────────────────────────────

interface StageItem {
  num: number;
  label: string;
  date: Date;
  dateKey: string;
  amount: number | null;
  pct?: number | null;
  note?: string;
}

const ScheduleStageCard: React.FC<{
  stage: StageItem;
  cardBorder: string;
  showDate: boolean;
  onDateChange: (key: string, val: string) => void;
  onPctChange?: (val: number) => void;
}> = ({ stage, cardBorder, showDate, onDateChange, onPctChange }) => {
  const [editingDate, setEditingDate] = useState(false);
  const [editingPct, setEditingPct] = useState(false);
  const [pctDraft, setPctDraft] = useState('');

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, p: 2, borderRadius: 2, bgcolor: 'background.paper', borderColor: cardBorder }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%', bgcolor: '#D97706', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: '0.8rem', flexShrink: 0, mt: 0.25,
        }}>
          {stage.num}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={700} sx={{ mr: 1 }}>{stage.label}</Typography>
            {stage.amount != null && (
              <Typography variant="body2" fontWeight={700} sx={{ color: '#D97706', whiteSpace: 'nowrap' }}>
                {fmt(stage.amount)}
              </Typography>
            )}
          </Box>

          {/* Percentage row (private only) */}
          {stage.pct != null && onPctChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              {editingPct ? (
                <TextField
                  size="small"
                  autoFocus
                  value={pctDraft}
                  onChange={(e) => setPctDraft(e.target.value)}
                  onBlur={() => {
                    setEditingPct(false);
                    const v = parseFloat(pctDraft);
                    if (!isNaN(v) && v >= 0) onPctChange(v);
                  }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ width: 90 }}
                  inputProps={{ style: { fontSize: '0.75rem', padding: '2px 6px' } }}
                />
              ) : (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{stage.pct.toFixed(2)}%</Typography>
                  <IconButton size="small" onClick={() => { setEditingPct(true); setPctDraft(String(stage.pct)); }}>
                    <EditIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </>
              )}
            </Box>
          )}

          {/* Date row (hidden when the timeline-dates toggle is off) */}
          {showDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {editingDate ? (
              <TextField
                size="small"
                type="date"
                autoFocus
                value={toDateStr(stage.date)}
                onChange={(e) => onDateChange(stage.dateKey, e.target.value)}
                onBlur={() => setEditingDate(false)}
                sx={{ width: 140 }}
                inputProps={{ style: { fontSize: '0.75rem', padding: '2px 6px' } }}
              />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDateDisplay(stage.date)}
                </Typography>
                <IconButton size="small" onClick={() => setEditingDate(true)}>
                  <EditIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </>
            )}
          </Box>
          )}

          {/* Note */}
          {stage.note && (
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
              {stage.note}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Timeline dates toggle (matches BuyingCalculator) ────────────────────────

// The "Show dates in timeline" switch above the sales schedule. Its value lives in
// SellingCalculator so the exported PDF drops dates alongside the screen.
const TimelineDatesToggle: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      Show dates in timeline
    </Typography>
    <ToggleButtonGroup
      value={value ? 'yes' : 'no'}
      exclusive
      onChange={(_, v) => { if (v) onChange(v === 'yes'); }}
      size="small"
      sx={{
        '& .MuiToggleButton-root': { px: 2, py: 0.4, fontWeight: 600, fontSize: '0.75rem' },
        '& .Mui-selected': { bgcolor: '#D97706 !important', color: '#fff !important', borderColor: '#D97706 !important' },
      }}
    >
      <ToggleButton value="yes">Yes</ToggleButton>
      <ToggleButton value="no">No</ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

// ─── Section Card Header (matches BuyingCalculator style) ────────────────────

const SectionCardHeader: React.FC<{
  title: string;
  step?: number;
  color: string;
  isDark: boolean;
  cardBorder: string;
}> = ({ title, step, color, isDark, cardBorder }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: 2.5, py: 1.5,
    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'grey.100',
    borderBottom: `1px solid ${cardBorder}`,
  }}>
    <Typography variant="subtitle1" fontWeight={800} sx={{
      color, letterSpacing: '0.08em',
      fontSize: '0.95rem', textTransform: 'uppercase',
    }}>
      {title}
    </Typography>
    {step !== undefined && (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
        Step {step}
      </Typography>
    )}
  </Box>
);

// ─── Tutorial ────────────────────────────────────────────────────────────────

/** Step-by-step guide shown by the cap button beside the page title. Each
 *  targetElement matches a `data-tutorial` attribute below, so "Show on Page"
 *  can scroll to and highlight the real control. */
const SELLING_TUTORIAL_STEPS: TutorialStepDef[] = [
  {
    title: 'Welcome to the Selling Calculator',
    content:
      'This calculator answers two questions for a seller: what do I walk away with, and when does each milestone fall due?\n\n' +
      'Enter the sale and its costs on the left; the net proceeds and the sales timeline update on the right. On a phone the results come first, with the inputs below them.\n\n' +
      'On any step below, tap "Show on Page" and the calculator will scroll to the control being described and pulse a highlight around it for a few seconds. Some steps apply to one tab only — switch to it first, and the highlight will find the control.',
    targetElement: null,
  },
  {
    title: 'Load Example / History',
    content:
      'Start from something rather than a blank form:\n' +
      '• Load Example / History: reopen any case you have saved before\n' +
      '• Save to History / Profile: with no profile loaded the button opens a chooser — history only, a new client profile built from the seller names, or both linked together. With a profile loaded it saves to history and writes the names back to the profile in one click\n' +
      '• Clear: resets the calculator to its defaults\n' +
      '• Export PDF: the proceeds breakdown and the full sales schedule, ready to send\n\n' +
      'History is shared across the Super Calculators, so the same case can be reopened in the Buying Calculator to plan the next purchase. Loading the profile later brings the whole sale back with it.',
    targetElement: '[data-tutorial="load-example"]',
  },
  {
    title: 'Property Type Tabs',
    content:
      'Private and HDB are two separate cases, each with its own figures, fees and timeline:\n' +
      '• Private: the option and exercise fees are a percentage of the sale price, and Seller’s Stamp Duty can be applied\n' +
      '• HDB: a fixed $1,000 option fee and $4,000 exercise fee, the HDB resale expenses, and the official HDB resale timeline\n\n' +
      'Each tab keeps its own numbers, so you can hold two properties side by side without retyping.',
    targetElement: '[data-tutorial="property-tabs"]',
  },
  {
    title: 'Calculator Mode: Simple vs Detailed',
    content:
      'How much the form asks about who owns the property:\n' +
      '• Simple: one owner. Step 2 is a single field — the CPF withdrawal with accrued interest — and the proceeds come out as one figure\n' +
      '• Detailed: a joint sale. Add a second owner, set the shares, and give each of them their own CPF withdrawal — a Proceeds Split card then shows what each walks away with\n\n' +
      'Everything else — the sale price, the expenses, stamp duty and the timeline — is identical in both. A second seller entered in Detailed is kept when you switch to Simple; it just sits out until you switch back.',
    targetElement: '[data-tutorial="calculator-mode"]',
  },
  {
    title: '1. Property',
    content:
      'The two figures that set the ceiling:\n' +
      '• Property Sale Price: what the buyer is paying\n' +
      '• Outstanding Loan: the mortgage to be redeemed on completion\n\n' +
      'The third figure — CPF Withdrawal With Accrued Interest — sits in Step 2 below, since in a joint sale each owner refunds their own.',
    targetElement: '[data-tutorial="property-section"]',
  },
  {
    title: '2. Expenses',
    content:
      'Everything deducted from the sale:\n' +
      '• Agent Service Fee: 2% of the sale price plus GST by default — tap the amount to change the rate, switch to a fixed fee, or drop GST\n' +
      '• Legal Fee: conveyancing, pre-filled at $3,000\n' +
      '• Bank Loan Penalty: the early-redemption penalty if the loan is still within its lock-in period\n' +
      '• HDB only — Resale Application ($80) and Misc Fee\n\n' +
      'Every entry here feeds the Total Expenses line in the Proceeds Breakdown, which expands to show them individually.',
    targetElement: '[data-tutorial="expenses-section"]',
  },
  {
    title: '3. Others (HDB tab)',
    content:
      'HDB-specific settings that shape the timeline:\n' +
      '• Resale Application Days: how long after exercising the OTP the resale application goes in (default 14) — every later milestone shifts with it\n' +
      '• HDB Upgrading Fee: any outstanding upgrading cost recovered on the sale\n' +
      '• 3-Month Extension: switch this on when the seller has already exercised the OTP on their next property. It adds 90 days to the handover date so they have time to move — completion is unchanged.',
    targetElement: '[data-tutorial="others-section"]',
  },
  {
    title: '3. Seller Stamp Duty (Private tab)',
    content:
      'SSD is payable when a property is sold within the applicable holding period. Switch it on and enter both dates:\n' +
      '• Purchase Date: when the seller acquired the property — the OTP being exercised, or the S&P signed\n' +
      '• Sale Date: when the buyer exercises the Option to Purchase\n\n' +
      'For purchases on or after 4 July 2025, the rates are 16%, 12%, 8% and 4% across the first four years. Purchases through 3 July 2025 retain the 12%, 8% and 4% three-year schedule. The band applied is shown under the calculated figure, and the amount is deducted from the proceeds.',
    targetElement: '[data-tutorial="ssd-section"]',
  },
  {
    title: 'Sales Proceeds',
    content:
      'The headline figure — what the seller actually receives once the loan is redeemed, CPF is refunded and every cost is paid.\n\n' +
      'If the sale does not cover all of that, the card shows a Shortfall in red instead. That is the amount the seller has to bring in cash to complete, and it is the number to solve before going any further.',
    targetElement: '[data-tutorial="sales-proceeds"]',
  },
  {
    title: 'Proceeds Breakdown',
    content:
      'The same figure, line by line:\n\n' +
      'Sale Price − Outstanding Loan − CPF Withdrawal (with accrued interest) − Total Expenses − Seller’s Stamp Duty = Net Sales Proceeds\n\n' +
      'Tap the Total Expenses row to expand it into the individual fees. The Seller’s Stamp Duty line only appears when SSD applies.',
    targetElement: '[data-tutorial="proceeds-breakdown"]',
  },
  {
    title: 'Display Controls',
    content:
      'Two viewing controls, both remembered across calculators:\n' +
      '• A- / A+: shrink or enlarge the text — useful when presenting figures to a client\n' +
      '• 1 Scroll / 2 Scroll (desktop): 1 Scroll moves the page as one column; 2 Scroll lets the inputs and the results scroll independently, so the proceeds stay in view while you adjust an input',
    targetElement: '[data-tutorial="display-controls"]',
  },
  {
    title: 'Sellers & the Split',
    content:
      'Who owns the property being sold:\n' +
      '• Simple mode asks one question — the CPF withdrawal with accrued interest. One owner takes 100% of the proceeds, so there is nothing else to name or divide\n' +
      '• Switch to Detailed for a joint sale and add a second seller: shares start at 50/50 and typing one balances the other\n' +
      '• Loading a profile fills this in — a joint profile opens two cards with the owners\u2019 names\n\n' +
      'Settlement runs in that order: the loan, expenses and stamp duty come off the sale, each owner\u2019s CPF goes back to their own OA, and the cash left over is divided by the shares. Two sellers add a Proceeds Split card on the right.',
    targetElement: '[data-tutorial="sellers-section"]',
  },
  {
    title: 'Show Dates in Timeline',
    content:
      'Set this to No while nothing is fixed yet — the timeline then shows only the sequence of milestones and what is paid at each. Set it to Yes once the OTP is signed and every milestone carries a real date.\n\n' +
      'The same setting controls whether dates appear in the exported PDF.',
    targetElement: '[data-tutorial="show-dates"]',
  },
  {
    title: 'Sales Schedule',
    content:
      'The milestone timeline, counted forward from the OTP date:\n' +
      '• Private: OTP → exercise, 14 days later → completion, 12 weeks after exercise. The option and exercise fees default to 1% and 4% of the sale price; tap the pencil beside either percentage to change it and the amount recalculates\n' +
      '• HDB: OTP ($1,000) → exercise, about 21 days later ($4,000) → resale application → HDB acceptance, about 28 working days → endorsement, about a week → approval, about a week → completion, 8 weeks from acceptance → handover\n\n' +
      'Tap the pencil beside any date to pin it to a firm date. Milestones after it are recalculated from what you set, so a confirmed HDB appointment reshapes the rest of the timeline.',
    targetElement: '[data-tutorial="sales-schedule"]',
  },
  {
    title: 'Carrying the Proceeds Forward',
    content:
      'The net proceeds do not stop here. Move to the Buying Calculator and it picks them up automatically:\n' +
      '• Each seller\u2019s cash share → the Sales Proceed row in Available Funds\n' +
      '• Each seller\u2019s CPF refund → Additional CPF\n' +
      '• Only sellers who are also buying carry over: drop a borrower there and their share leaves the funds with them. Tap the info icon on either row to see who was counted\n' +
      '• Selling an HDB flat also flags the EC resale levy and the second HDB loan cash set-aside\n\n' +
      'So the next purchase is planned on the seller’s real numbers rather than an estimate.\n\n' +
      'Your place in this tutorial is remembered — the cap button beside the title is red before you start, amber while in progress and green once finished. Use Restart to run through it again, or the download icon in the header for the full text manual.',
    targetElement: '[data-tutorial="load-example"]',
  },
];

/** Reference material appended to the downloaded manual, after the steps. */
const SELLING_MANUAL_SECTIONS = [
  {
    heading: 'Net Proceeds Formula',
    body:
      'Net Sales Proceeds =\n'
      + '    Sale Price\n'
      + '  - Outstanding Loan\n'
      + '  - CPF Withdrawal (principal + accrued interest)\n'
      + '  - Total Expenses\n'
      + '  - Seller\u2019s Stamp Duty\n\n'
      + 'A negative result is shown as a Shortfall: the cash the seller must bring\n'
      + 'to complete the sale.',
  },
  {
    heading: 'Seller\u2019s Stamp Duty Rates',
    body:
      'Holding period is measured from the purchase date to the sale date.\n\n'
      + 'Purchased on or after 4 Jul 2025:\n'
      + '  Held under 1 year      16% of the sale price\n'
      + '  Held 1 to 2 years      12% of the sale price\n'
      + '  Held 2 to 3 years       8% of the sale price\n'
      + '  Held 3 to 4 years       4% of the sale price\n'
      + '  Held 4 years or more    No SSD\n\n'
      + 'Purchased through 3 Jul 2025:\n'
      + '  Held under 1 year      12% of the sale price\n'
      + '  Held 1 to 2 years       8% of the sale price\n'
      + '  Held 2 to 3 years       4% of the sale price\n'
      + '  Held 3 years or more    No SSD\n\n'
      + 'SSD is applied on the Private tab only, via the Seller Stamp Duty switch.',
  },
  {
    heading: 'Timelines',
    body:
      'Private resale:\n'
      + '  OTP -> exercise (14 days) -> completion (12 weeks after exercise).\n'
      + '  Option fee defaults to 1% and the exercise fee to 4% of the sale price;\n'
      + '  both percentages are editable in the schedule.\n\n'
      + 'HDB resale:\n'
      + '  1. OTP, option fee $1,000. HDB loan: HFE approved before OTP.\n'
      + '  2. Exercise OTP, exercise fee $4,000, about 21 days after OTP. Bank loan:\n'
      + '     Letter of Offer signed before exercise.\n'
      + '  3. Resale Application, by default 14 days after exercise (adjustable).\n'
      + '     Either party may submit first; the other applies within 7 days.\n'
      + '  4. HDB Acceptance (3.1), about 28 working days later. HDB gives the\n'
      + '     completion date here.\n'
      + '  5. HDB Endorsement (3.2), about 1 week later.\n'
      + '  6. HDB Approval (3.3), about 1 week later.\n'
      + '  7. Completion, 8 weeks from HDB Acceptance. Physical appointment at HDB;\n'
      + '     bring the original OTP.\n'
      + '  8. Handover of keys to the buyer, on the completion date (+90 days with\n'
      + '     the 3-month extension).\n\n'
      + 'Any milestone date can be pinned by hand; later milestones recalculate\n'
      + 'from the pinned date.',
  },
  {
    heading: 'Sources',
    body:
      'IRAS - Seller\u2019s Stamp Duty for residential property.\n'
      + 'HDB - resale procedure, resale application and completion appointments.\n'
      + 'CPF Board - CPF refund on sale, principal plus accrued interest.',
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SellingCalculator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const linkedCalc = useLinkedCalculator();
  const { selectedProfile, updateProfile } = useProfile();
  const currentUser = useCurrentUser();

  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';

  const [propertyTab, setPropertyTab] = useState<PropertyTab>('private');
  const [calculatorMode, setCalculatorMode] = useState<SellingCalculatorMode>('simple');
  const [hdbInputs, setHdbInputs] = useState<SellingInputs>(DEFAULT_HDB);
  const [privateInputs, setPrivateInputs] = useState<SellingInputs>(DEFAULT_PRIVATE);

  const [otpPercent, setOtpPercent] = useState(1);
  const [exercisePercent, setExercisePercent] = useState(4);

  const [hdbDates, setHdbDates] = useState<HdbDates>({
    otp: TODAY_STR, exercise: '', resaleApp: '', acceptance: '',
    endorsement: '', approval: '', completion: '', handover: '',
  });
  const [privateDates, setPrivateDates] = useState<PrivateDates>({ otp: TODAY_STR, exercise: '', completion: '' });

  const [hdbExtension, setHdbExtension] = useState(false);
  const [hdbResaleAppDays, setHdbResaleAppDays] = useState(14);
  const [privateStampDutyEnabled, setPrivateStampDutyEnabled] = useState(false);
  const [ssdInfoAnchor, setSsdInfoAnchor] = useState<HTMLElement | null>(null);
  const [showTimeline, setShowTimeline] = useState(true);
  const [showExpenseBreakdown, setShowExpenseBreakdown] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  // Scroll mode: false = the page scrolls as one column (default); true = the inputs
  // and the results scroll independently (desktop only).
  const [splitScroll, setSplitScroll] = useState(false);
  // Agent fee editor — opened by tapping the amount on the Expenses card.
  const [agentFeeDialogOpen, setAgentFeeDialogOpen] = useState(false);
  const [agentFeeDraftMode, setAgentFeeDraftMode] = useState<'pct' | 'amount'>('pct');
  const [agentFeeDraftPct, setAgentFeeDraftPct] = useState('2');
  const [agentFeeDraftAmount, setAgentFeeDraftAmount] = useState(0);
  const [agentFeeDraftGst, setAgentFeeDraftGst] = useState(true);

  // Step-by-step tutorial (cap button beside the page title).
  const tutorial = useCalculatorTutorial('sellingCalculatorTutorialProgress', SELLING_TUTORIAL_STEPS.length);
  // Announces the newest release once per agent (acknowledgement stored server-side),
  // then sits behind the megaphone button.
  const whatsNew = useWhatsNew('selling', SELLING_WHATS_NEW);

  // Dynamic slider max for sale price — auto-extends when value exceeds default.
  const [salePriceSliderMax, setSalePriceSliderMax] = useState<number>(() =>
    Math.max(5_000_000, Math.ceil(((privateInputs.salePrice + hdbInputs.salePrice) / 2 || 0) / 100_000) * 100_000)
  );

  // ── Linked Calculator: register state getter/setter ───────────────────
  const sellingSnapshotRef = useRef<Record<string, unknown>>({});
  sellingSnapshotRef.current = {
    propertyTab, calculatorMode, hdbInputs, privateInputs,
    otpPercent, exercisePercent,
    hdbDates, privateDates,
    hdbExtension, hdbResaleAppDays, privateStampDutyEnabled, showTimeline,
  };

  useEffect(() => {
    linkedCalc.registerStateGetter('selling', () => sellingSnapshotRef.current);
  }, [linkedCalc]);

  useEffect(() => {
    linkedCalc.registerStateSetter('selling', (state: unknown) => {
      const s = state as Record<string, unknown> | null;
      if (!s) return;
      if (s.propertyTab) setPropertyTab(s.propertyTab as PropertyTab);
      const restoredHdb = s.hdbInputs ? normalizeSellingInputs(s.hdbInputs, DEFAULT_HDB) : null;
      const restoredPrivate = s.privateInputs ? normalizeSellingInputs(s.privateInputs, DEFAULT_PRIVATE) : null;
      if (restoredHdb) setHdbInputs(restoredHdb);
      if (restoredPrivate) setPrivateInputs(restoredPrivate);
      // Entries saved before the Simple/Detailed switch existed carry no mode. A sale
      // split between two owners is a detailed case; anything else opens in Simple.
      if (s.calculatorMode === 'simple' || s.calculatorMode === 'detailed') {
        setCalculatorMode(s.calculatorMode);
      } else {
        const wasSplit = [restoredHdb, restoredPrivate].some((i) => (i?.sellers.length ?? 0) > 1);
        setCalculatorMode(wasSplit ? 'detailed' : 'simple');
      }
      if (typeof s.otpPercent === 'number') setOtpPercent(s.otpPercent);
      if (typeof s.exercisePercent === 'number') setExercisePercent(s.exercisePercent);
      if (s.hdbDates) setHdbDates(s.hdbDates as HdbDates);
      if (s.privateDates) setPrivateDates(s.privateDates as PrivateDates);
      if (typeof s.hdbExtension === 'boolean') setHdbExtension(s.hdbExtension);
      if (typeof s.hdbResaleAppDays === 'number') setHdbResaleAppDays(s.hdbResaleAppDays);
      if (typeof s.privateStampDutyEnabled === 'boolean') setPrivateStampDutyEnabled(s.privateStampDutyEnabled);
      if (typeof s.showTimeline === 'boolean') setShowTimeline(s.showTimeline);
    });
  }, [linkedCalc]);

  useEffect(() => {
    // Selling calculator has no borrower profiles — return empty
    linkedCalc.registerProfileBuilder('selling', () => []);
  }, [linkedCalc]);

  const inputs = propertyTab === 'hdb' ? hdbInputs : privateInputs;
  const isSimple = calculatorMode === 'simple';

  /** The owners the sale is actually settled against. Simple mode is a one-owner
   *  sale: the first seller takes the whole proceeds and their CPF withdrawal is
   *  the only refund. A second seller added in Detailed is left untouched in state
   *  — it just sits out until the mode is switched back. */
  const effectiveSellers = useMemo<SellerState[]>(
    () => (isSimple ? [{ ...inputs.sellers[0], sharePct: 100 }] : inputs.sellers),
    [isSimple, inputs.sellers],
  );

  /** The seller cards on screen. Simple mode shows the first owner only; the card
   *  edits `inputs.sellers[0]` directly, so nothing typed here is lost on a switch. */
  const visibleSellers = isSimple ? inputs.sellers.slice(0, 1) : inputs.sellers;

  // Auto-extend sale-price slider max if the value exceeds it.
  useEffect(() => {
    const needed = Math.ceil(inputs.salePrice / 100_000) * 100_000;
    if (needed > salePriceSliderMax) setSalePriceSliderMax(needed);
  }, [inputs.salePrice, salePriceSliderMax]);

  // Restore every Selling Calculator value to the same defaults used on first load.
  const clearCalculator = useCallback(() => {
    setPropertyTab('private');
    setCalculatorMode('simple');
    setHdbInputs(DEFAULT_HDB);
    setPrivateInputs(DEFAULT_PRIVATE);
    setOtpPercent(1);
    setExercisePercent(4);
    setHdbDates({
      otp: TODAY_STR, exercise: '', resaleApp: '', acceptance: '',
      endorsement: '', approval: '', completion: '', handover: '',
    });
    setPrivateDates({ otp: TODAY_STR, exercise: '', completion: '' });
    setHdbExtension(false);
    setHdbResaleAppDays(14);
    setPrivateStampDutyEnabled(false);
    setSsdInfoAnchor(null);
    setShowTimeline(true);
    setShowExpenseBreakdown(false);
    setSalePriceSliderMax(5_000_000);
    setSplitScroll(false);

    // Clear the active example/history selection. Saved history entries remain intact.
    linkedCalc.setSelectedExample('');
    linkedCalc.setActiveSelection(null);
    linkedCalc.showToast('Selling Calculator reset');
    setClearDialogOpen(false);
  }, [linkedCalc]);

  const updateInputs = useCallback(<K extends keyof SellingInputs>(field: K, value: SellingInputs[K]) => {
    if (propertyTab === 'hdb') {
      setHdbInputs(prev => ({ ...prev, [field]: value }));
    } else {
      setPrivateInputs(prev => ({ ...prev, [field]: value }));
    }
  }, [propertyTab]);

  /** Commits several fields of the active tab at once (the agent-fee dialog). */
  const setInputsPatch = useCallback((patch: Partial<SellingInputs>) => {
    if (propertyTab === 'hdb') {
      setHdbInputs(prev => ({ ...prev, ...patch }));
    } else {
      setPrivateInputs(prev => ({ ...prev, ...patch }));
    }
  }, [propertyTab]);

  /** Edits one seller on the active tab. */
  const updateSeller = useCallback((index: number, patch: Partial<SellerState>) => {
    const apply = (prev: SellingInputs): SellingInputs => ({
      ...prev,
      sellers: prev.sellers.map((seller, i) => (i === index ? { ...seller, ...patch } : seller)),
    });
    if (propertyTab === 'hdb') setHdbInputs(apply); else setPrivateInputs(apply);
  }, [propertyTab]);

  /** Typing one share balances the other, so the two always total 100%. */
  const updateSellerShare = useCallback((index: number, raw: string) => {
    const pct = Math.max(0, Math.min(100, Number.parseFloat(raw.replace(/[^0-9.]/g, '')) || 0));
    const apply = (prev: SellingInputs): SellingInputs => ({
      ...prev,
      sellers: prev.sellers.map((seller, i) =>
        i === index
          ? { ...seller, sharePct: pct }
          : prev.sellers.length === 2 ? { ...seller, sharePct: Math.round((100 - pct) * 100) / 100 } : seller
      ),
    });
    if (propertyTab === 'hdb') setHdbInputs(apply); else setPrivateInputs(apply);
  }, [propertyTab]);

  /** A second seller starts on an even split — the common case for a couple. */
  const addSeller = useCallback(() => {
    const apply = (prev: SellingInputs): SellingInputs =>
      prev.sellers.length >= 2 ? prev : {
        ...prev,
        sellers: [{ ...prev.sellers[0], sharePct: 50 }, createSeller(50)],
      };
    if (propertyTab === 'hdb') setHdbInputs(apply); else setPrivateInputs(apply);
  }, [propertyTab]);

  /** Removing a seller hands the whole sale to the one who remains. */
  const removeSeller = useCallback((index: number) => {
    const apply = (prev: SellingInputs): SellingInputs => {
      const remaining = prev.sellers.filter((_, i) => i !== index);
      if (remaining.length === 0) return prev;
      return { ...prev, sellers: remaining.map((seller) => ({ ...seller, sharePct: 100 })) };
    };
    if (propertyTab === 'hdb') setHdbInputs(apply); else setPrivateInputs(apply);
  }, [propertyTab]);

  // ─── Writing back to the profile ──────────────────────────────────────────
  // This page only ever learns one thing about a client: who they are. Every other
  // figure here belongs to the sale, not the person — most of all the CPF withdrawal,
  // which is the amount sunk into THIS property plus accrued interest, not an OA
  // balance. Writing it to `Cpf` would corrupt the profile for every other
  // calculator, so the save-back is deliberately limited to names. The sale itself
  // is kept by the history entry, tagged with this profile.
  const saveInputsToProfile = useCallback(async (): Promise<boolean> => {
    if (!selectedProfile) return false;
    const members = selectedProfile.type === 'profile'
      ? [selectedProfile.profile]
      : (selectedProfile.group.profiles || []);
    if (members.length === 0) return false;

    beginProfileSaveBack();
    try {
      for (let i = 0; i < Math.min(members.length, effectiveSellers.length); i++) {
        const name = effectiveSellers[i]?.name?.trim();
        // An untouched card must not blank out the name the profile already has.
        if (!name || name === members[i].Name) continue;
        await updateProfile(members[i].Id, { Name: name });
      }
      return true;
    } catch (err) {
      console.error('Failed to save inputs to profile:', err);
      return false;
    } finally {
      endProfileSaveBack();
    }
  }, [selectedProfile, effectiveSellers, updateProfile]);

  /** Seeds "Save to Profile" when no profile is loaded yet — one member per seller
   *  on the sale. Names can still be filled in (or corrected) in the save dialog. */
  const buildNewProfilesFromInputs = useCallback((): NewProfileDraft | null => {
    const names = effectiveSellers.map((seller) => seller.name?.trim() || '');
    return {
      members: names.map((name) => ({ Name: name })),
      groupName: names.filter(Boolean).join(' & '),
      relationship: names.length > 1 ? 'SPOUSE' : 'INDIVIDUAL',
    };
  }, [effectiveSellers]);

  // ─── Seller cards follow the loaded profile ────────────────────────────────
  // A group means a joint sale: one card per member, named from the profile. A
  // single profile collapses to one seller at 100%. Only the names and the number
  // of cards come from the profile — the CPF figure is this sale's own, since the
  // profile's CPF field is an OA balance, not a withdrawal with accrued interest.
  const selectedMemberNames = useMemo<string[]>(() => {
    if (!selectedProfile) return [];
    const members =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : (selectedProfile.group.profiles || []);
    return members.slice(0, 2).map((m) => m.Name?.trim() || '');
  }, [selectedProfile]);

  useEffect(() => {
    if (selectedMemberNames.length === 0) return;
    // Stand aside while this page is writing names back to the profile, and while a
    // queued history load is waiting — the saved snapshot owns the seller cards then.
    if (isProfileSaveBackInProgress() || hasPendingHistoryLoad()) return;
    const apply = (prev: SellingInputs): SellingInputs => {
      const next = selectedMemberNames.map((name, idx) => {
        const existing = prev.sellers[idx];
        const evenShare = selectedMemberNames.length > 1 ? 50 : 100;
        return existing
          ? { ...existing, name: name || existing.name, sharePct: prev.sellers.length === selectedMemberNames.length ? existing.sharePct : evenShare }
          : { ...createSeller(evenShare), name };
      });
      const unchanged =
        next.length === prev.sellers.length &&
        next.every((seller, i) => seller.name === prev.sellers[i].name && seller.sharePct === prev.sellers[i].sharePct);
      return unchanged ? prev : { ...prev, sellers: next };
    };
    setHdbInputs(apply);
    setPrivateInputs(apply);
  }, [selectedMemberNames]);

  // ─── SSD (private only, when toggle enabled — HDB has no SSD) ──────────────
  const ssd = useMemo(
    () => (propertyTab === 'private' && privateStampDutyEnabled
      ? calcSSD(inputs.purchaseDate, inputs.saleDate, inputs.salePrice)
      : 0),
    [inputs, propertyTab, privateStampDutyEnabled]
  );

  // ─── Agent fee: 2% of the sale price incl. GST unless the seller edits it ──
  const effectiveAgentFee = computeAgentFee(inputs);
  const agentFeeBase = inputs.agentFeeMode === 'pct'
    ? Math.round((inputs.salePrice * (inputs.agentFeePct || 0)) / 100)
    : Math.round(inputs.agentFee);
  const agentFeeLabel = inputs.agentFeeMode === 'pct'
    ? `Agent Service Fee (${inputs.agentFeePct === DEFAULT_AGENT_FEE_PCT ? 'Default ' : ''}`
      + `${Number(inputs.agentFeePct.toFixed(2))}%${inputs.agentFeeGstInclusive ? ', inclusive GST' : ''})`
    : `Agent Service Fee${inputs.agentFeeGstInclusive ? ' (inclusive GST)' : ''}`;

  const openAgentFeeDialog = () => {
    setAgentFeeDraftMode(inputs.agentFeeMode);
    setAgentFeeDraftPct(String(Number(inputs.agentFeePct.toFixed(2))));
    setAgentFeeDraftAmount(agentFeeBase);
    setAgentFeeDraftGst(inputs.agentFeeGstInclusive);
    setAgentFeeDialogOpen(true);
  };

  const commitAgentFee = () => {
    const pct = Math.max(0, Math.min(100, Number.parseFloat(agentFeeDraftPct) || 0));
    setInputsPatch({
      agentFeeMode: agentFeeDraftMode,
      agentFeePct: agentFeeDraftMode === 'pct' ? pct : inputs.agentFeePct,
      agentFee: agentFeeDraftMode === 'amount' ? Math.max(0, Math.round(agentFeeDraftAmount)) : inputs.agentFee,
      agentFeeGstInclusive: agentFeeDraftGst,
    });
    setAgentFeeDialogOpen(false);
    logCalculatorInteraction('selling_calculator', 'mode_switch', {
      field: 'agent_fee',
      basis: agentFeeDraftMode,
      gst: agentFeeDraftGst,
    });
  };

  // What the dialog is about to charge, shown as its working.
  const agentFeeDraftBase = agentFeeDraftMode === 'pct'
    ? Math.round((inputs.salePrice * (Number.parseFloat(agentFeeDraftPct) || 0)) / 100)
    : Math.max(0, Math.round(agentFeeDraftAmount));
  const agentFeeDraftGstAmount = agentFeeDraftGst ? Math.round(agentFeeDraftBase * (AGENT_FEE_GST - 1)) : 0;

  // ─── Totals ────────────────────────────────────────────────────────────────
  const expenseItems = useMemo(() => {
    const items: { label: string; amount: number }[] = [];
    if (propertyTab === 'hdb' && inputs.resaleApplication > 0) {
      items.push({ label: 'Resale Application', amount: inputs.resaleApplication });
    }
    if (effectiveAgentFee > 0) {
      items.push({ label: 'Agent Service Fee', amount: effectiveAgentFee });
    }
    if (inputs.legalFee > 0) {
      items.push({ label: 'Legal Fee', amount: inputs.legalFee });
    }
    if (propertyTab === 'hdb' && inputs.miscFee > 0) {
      items.push({ label: 'Misc Fee', amount: inputs.miscFee });
    }
    if (inputs.bankLoanPenalty > 0) {
      items.push({ label: 'Bank Loan Penalty', amount: inputs.bankLoanPenalty });
    }
    if (propertyTab === 'hdb' && inputs.hdbUpgradingFee > 0) {
      items.push({ label: 'HDB Upgrading Fee', amount: inputs.hdbUpgradingFee });
    }
    return items;
  }, [inputs, propertyTab, effectiveAgentFee]);

  const totalExpenses = useMemo(
    () => expenseItems.reduce((sum, item) => sum + item.amount, 0),
    [expenseItems]
  );

  // ─── Sellers & the split ───────────────────────────────────────────────────
  // Settlement order, as conveyancing handles it: the loan, expenses and SSD come off
  // the sale, then every owner's CPF is refunded to their own OA, and the cash left
  // over is divided by the ownership shares.
  const cpfRefundTotal = totalCpfRefund(effectiveSellers);
  const sellerShareTotal = inputs.sellers.reduce((sum, seller) => sum + (seller.sharePct || 0), 0);

  const salesProceeds = useMemo(() =>
    inputs.salePrice - inputs.outstandingLoan - cpfRefundTotal - totalExpenses - ssd,
    [inputs.salePrice, inputs.outstandingLoan, cpfRefundTotal, totalExpenses, ssd]
  );

  const sellerSplit = useMemo(() => {
    const sellers = effectiveSellers;
    const netCash = Math.round(salesProceeds);
    let allocated = 0;
    return sellers.map((seller, idx) => {
      // The last seller absorbs the rounding remainder, so the shares always add
      // back up to the net cash proceeds exactly.
      const cash = idx === sellers.length - 1
        ? netCash - allocated
        : Math.round((netCash * (seller.sharePct || 0)) / 100);
      allocated += cash;
      const cpfRefund = Math.max(0, Math.round(seller.cpfWithdrawal));
      return {
        name: seller.name?.trim() || (sellers.length > 1 ? `Seller ${idx + 1}` : 'Seller'),
        sharePct: seller.sharePct || 0,
        cash,
        cpfRefund,
        // What this owner walks away with, across both pockets.
        total: cash + cpfRefund,
      };
    });
  }, [effectiveSellers, salesProceeds]);

  // Keep latest proceeds + CPF withdrawal accessible to the cross-calc extractor
  // (registered once, but must read fresh values on each navigation).
  const proceedsRef = useRef<SharedProceedsData>({ netSalesProceeds: 0, cpfWithdrawal: 0, soldHdb: false });
  proceedsRef.current = {
    netSalesProceeds: Math.max(0, Math.round(salesProceeds)),
    cpfWithdrawal: Math.max(0, Math.round(cpfRefundTotal)),
    soldHdb: propertyTab === 'hdb',
    // Buying keeps only the sellers who are also borrowers, so send them named.
    // A seller with no name typed is sent nameless on purpose — Buying then matches
    // by position rather than against a placeholder like "Seller 1".
    sellers: sellerSplit.map((seller, idx) => ({
      name: effectiveSellers[idx]?.name?.trim() || undefined,
      cashProceeds: seller.cash,
      cpfRefund: seller.cpfRefund,
    })),
  };

  useEffect(() => {
    linkedCalc.registerProceedsExtractor('selling', () => proceedsRef.current);
  }, [linkedCalc]);

  // ─── HDB Schedule (official HDB timeline) ────────────────────────────────
  const hdbSchedule = useMemo((): StageItem[] => {
    const otp         = parseDate(hdbDates.otp)         || new Date();
    const exercise    = parseDate(hdbDates.exercise)    || addDays(otp, 21);         // ~21 days
    const resaleApp   = parseDate(hdbDates.resaleApp)   || addDays(exercise, hdbResaleAppDays);
    const acceptance  = parseDate(hdbDates.acceptance)  || addWorkingDays(resaleApp, 28); // ~28 working days
    const endorsement = parseDate(hdbDates.endorsement) || addDays(acceptance, 7);   // 1 week
    const approval    = parseDate(hdbDates.approval)    || addDays(endorsement, 7);  // 1 week
    const completion  = parseDate(hdbDates.completion)  || addDays(acceptance, 56);   // 8 weeks from acceptance
    // The extension buys the seller time to move out, so it pushes the handing over of
    // keys — completion (the HDB appointment) still lands 8 weeks from Acceptance.
    const handover    = parseDate(hdbDates.handover)
      || (hdbExtension ? addDays(completion, 90) : completion);
    return [
      { num: 1, label: 'OTP (Option Fee)', date: otp, dateKey: 'otp', amount: 1000,
        note: 'HDB Loan: HFE approved before OTP' },
      { num: 2, label: 'Exercise OTP (Exercise Fee)', date: exercise, dateKey: 'exercise', amount: 4000,
        note: 'Bank Loan: Letter of Offer signed before exercise. ~21 days after OTP.' },
      { num: 3, label: 'Resale Application', date: resaleApp, dateKey: 'resaleApp', amount: null,
        note: `${hdbResaleAppDays} days after Exercise OTP. Either party may submit first; 2nd party applies within 7 days.` },
      { num: 4, label: 'HDB Acceptance (3.1)', date: acceptance, dateKey: 'acceptance', amount: null,
        note: 'Approximately 28 working days after Resale Application. HDB informs Completion Date here.' },
      { num: 5, label: 'HDB Endorsement (3.2)', date: endorsement, dateKey: 'endorsement', amount: null,
        note: 'Approximately 1 week after HDB Acceptance.' },
      { num: 6, label: 'HDB Approval (3.3)', date: approval, dateKey: 'approval', amount: null,
        note: 'Approximately 1 week after HDB Endorsement.' },
      { num: 7, label: 'Completion', date: completion, dateKey: 'completion', amount: null,
        note: '8 weeks from HDB Acceptance (3.1). Bring original OTP. Physical appt at HDB.' },
      { num: 8, label: 'Handover', date: handover, dateKey: 'handover', amount: null,
        note: hdbExtension
          ? 'Keys handed over to buyer — 3-month extension, 90 days after Completion.'
          : 'Keys handed over to buyer.' },
    ];
  }, [hdbDates, hdbExtension, hdbResaleAppDays]);

  // ─── Private Schedule ─────────────────────────────────────────────────────
  const privateSchedule = useMemo((): StageItem[] => {
    const otp = parseDate(privateDates.otp) || new Date();
    const exercise = parseDate(privateDates.exercise) || addDays(otp, 14);
    const completion = parseDate(privateDates.completion) || addDays(exercise, 84);
    const otpAmt = Math.round(inputs.salePrice * otpPercent / 100);
    const exerciseAmt = Math.round(inputs.salePrice * exercisePercent / 100);
    return [
      { num: 1, label: 'OTP (Option Fee)', date: otp, dateKey: 'otp', amount: otpAmt, pct: otpPercent, note: '' },
      { num: 2, label: `Exercise OTP (Exercise Fee ${exercisePercent}%)`, date: exercise, dateKey: 'exercise', amount: exerciseAmt, pct: exercisePercent, note: '' },
      { num: 3, label: 'Completion', date: completion, dateKey: 'completion', amount: null, pct: null, note: 'Return of sales proceeds (if any)' },
    ];
  }, [privateDates, inputs.salePrice, otpPercent, exercisePercent]);

  const updateHdbDate = useCallback((key: string, val: string) => {
    setHdbDates(prev => ({ ...prev, [key]: val }));
  }, []);

  const updatePrivateDate = useCallback((key: string, val: string) => {
    setPrivateDates(prev => ({ ...prev, [key]: val }));
  }, []);

  // ── PDF Export ─────────────────────────────────────────────────────────────
  const handleExportPdf = useCallback(() => {
    logPdfExport('selling_calculator', currentUser);

    // Condensed, ASCII-safe notes sized for the PDF's narrow milestone cards
    // (the on-screen notes are too long for 8 cards across a landscape page).
    const pdfNotes: Record<string, string> = propertyTab === 'hdb' ? {
      otp: 'HDB loan: HFE approved before OTP.',
      exercise: '~21 days after OTP. Bank loan: Letter of Offer signed.',
      resaleApp: `${hdbResaleAppDays} days after Exercise OTP.`,
      acceptance: '~28 working days after Resale Application.',
      endorsement: '~1 week after HDB Acceptance.',
      approval: '~1 week after HDB Endorsement.',
      completion: '8 weeks from HDB Acceptance. Physical appt at HDB.',
      handover: hdbExtension
        ? 'Keys to buyer - 3-mth extension, 90 days after Completion.'
        : 'Keys handed over to buyer.',
    } : {
      otp: 'Option fee received from buyer.',
      exercise: 'Within 14 days of OTP.',
      completion: 'Return of sales proceeds (if any).',
    };

    const activeSchedule = propertyTab === 'hdb' ? hdbSchedule : privateSchedule;
    const scheduleRows: SellingScheduleRowPdf[] = activeSchedule.map((s) => ({
      num: s.num,
      label: s.label,
      date: formatDateDisplay(s.date),
      amount: s.amount ?? undefined,
      note: pdfNotes[s.dateKey] || undefined,
    }));

    const ssdRateLabel = (() => {
      if (propertyTab !== 'private' || !privateStampDutyEnabled) return undefined;
      return calculateSellerStampDuty(
        inputs.purchaseDate,
        inputs.saleDate,
        inputs.salePrice,
      )?.rateLabel;
    })();

    const fmtInputDate = (str: string): string => {
      const d = parseDate(str);
      return d ? formatDateDisplay(d) : '';
    };

    // Returned, not fire-and-forget: the Export PDF button awaits it to run its progress bar.
    return exportSellingToPDF({
      inputs: {
        propertyTab,
        salePrice: inputs.salePrice,
        outstandingLoan: inputs.outstandingLoan,
        cpfWithdrawal: cpfRefundTotal,
        ssdEnabled: propertyTab === 'private' && privateStampDutyEnabled,
        purchaseDate: fmtInputDate(inputs.purchaseDate),
        saleDate: fmtInputDate(inputs.saleDate),
        ssdRateLabel,
      },
      expenses: expenseItems,
      // Only a joint sale needs the split table.
      sellers: effectiveSellers.length > 1
        ? sellerSplit.map((seller) => ({
            name: seller.name,
            sharePct: seller.sharePct,
            total: seller.total,
            cpfRefund: seller.cpfRefund,
            cash: seller.cash,
          }))
        : undefined,
      totalExpenses,
      ssd,
      netProceeds: Math.round(salesProceeds),
      schedule: scheduleRows,
      showDates: showTimeline,
      agentInfo: currentUser ?? undefined,
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to export selling calculator PDF', err);
    });
  }, [
    currentUser, propertyTab, hdbSchedule, privateSchedule, privateStampDutyEnabled,
    inputs, expenseItems, totalExpenses, ssd, salesProceeds, hdbResaleAppDays, hdbExtension,
    showTimeline, cpfRefundTotal, sellerSplit, effectiveSellers,
  ]);

  // ─── Tab colors ───────────────────────────────────────────────────────────
  const tabColor = propertyTab === 'hdb' ? '#D97706' : '#1e3a5f';
  const tabBg = propertyTab === 'hdb' ? '#D97706' : '#1e3a5f';

  const sectionCardSx = {
    mb: 2,
    overflow: 'hidden' as const,
    borderRadius: 2,
    borderColor: cardBorder,
  };
  const sectionBodySx = { p: 2.5 };

  // Dynamic slider maxes derived from current sale price.
  const loanSliderMax = Math.max(inputs.salePrice || 0, 1_500_000);

  // With split-scroll on (desktop only), each column scrolls in its own region, so the
  // proceeds stay in view while the inputs are filled in. Column cap: 64px app bar +
  // 24px container bottom padding + 24px breathing room at the top.
  const columnScrollSx = (splitScroll && !isMobile)
    ? { maxHeight: 'calc(100vh - 112px)', overflowY: 'auto' as const, pr: 1 }
    : undefined;

  // ─── Left panel ───────────────────────────────────────────────────────────
  const leftPanel = (
    <Box>
      {/* ── CALCULATOR MODE ── */}
      <Box
        data-tutorial="calculator-mode"
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
          Calculator Mode
        </Typography>
        <ToggleButtonGroup
          value={calculatorMode}
          exclusive
          onChange={(_, v) => {
            if (!v) return;
            setCalculatorMode(v);
            logCalculatorInteraction('selling_calculator', 'mode_switch', { mode: v });
          }}
          size="small"
          sx={{
            alignSelf: { xs: 'stretch', sm: 'center' },
            '& .MuiToggleButton-root': { px: 2.5, py: 0.5, fontWeight: 700, fontSize: '0.78rem', flex: { xs: 1, sm: 'initial' } },
            '& .Mui-selected': { bgcolor: `${tabColor} !important`, color: '#fff !important', borderColor: `${tabColor} !important` },
          }}
        >
          <ToggleButton value="simple">Simple</ToggleButton>
          <ToggleButton value="detailed">Detailed</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── PROPERTY ── */}
      <Card data-tutorial="property-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Property" step={1} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <SliderMoneyInput
            label="Property Sale Price"
            value={inputs.salePrice}
            onChange={(v) => updateInputs('salePrice', v)}
            max={salePriceSliderMax}
            commitMax={999_999_999}
            step={10_000}
            bigStep={50_000}
            color={tabColor}
            isMobile={isMobile}
          />
          <SliderMoneyInput
            label="Outstanding Loan"
            value={inputs.outstandingLoan}
            onChange={(v) => updateInputs('outstandingLoan', v)}
            max={loanSliderMax}
            step={10_000}
            bigStep={50_000}
            color={tabColor}
            isMobile={isMobile}
          />
        </Box>
      </Card>

      {/* ── SELLERS ── */}
      {/* Simple mode asks one question — the CPF to refund — so it drops the seller
          cards entirely rather than wrapping a lone field in a card of its own. */}
      <Card data-tutorial="sellers-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title={isSimple ? 'CPF Refund' : 'Sellers'} step={2} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            {isSimple
              ? 'The CPF withdrawn for this property, with accrued interest. It is refunded to the seller\u2019s OA on completion; the cash that remains is the net proceeds.'
              : 'Who owns this property. Each seller\u2019s CPF is refunded to their own OA, then the cash that remains is split by their shares.'}
          </Typography>

          {isSimple ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>CPF Withdrawal</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>With accrued interest</Typography>
              </Box>
              <Box sx={{ width: SELLER_FIELD_WIDTH, flexShrink: 0 }}>
                <CurrencyTextField
                  value={inputs.sellers[0]?.cpfWithdrawal ?? 0}
                  onChange={(v) => updateSeller(0, { cpfWithdrawal: Math.max(0, Math.round(v)) })}
                  highlightColor={tabColor}
                />
              </Box>
            </Box>
          ) : visibleSellers.map((seller, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: cardBorder,
                bgcolor: cardBg,
              }}
            >
              {/* Card header — the remove control sits here, so it never pushes the
                  fields below out of their shared right edge. */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', letterSpacing: '0.02em', color: 'text.secondary', textTransform: 'uppercase' }}>
                  {visibleSellers.length > 1 ? `Seller ${idx + 1}` : 'Seller'}
                </Typography>
                {visibleSellers.length > 1 && (
                  <IconButton
                    size="small"
                    aria-label={`Remove seller ${idx + 1}`}
                    onClick={() => removeSeller(idx)}
                    sx={{ width: 24, height: 24, color: 'text.secondary' }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
              <Divider sx={{ mb: 1.5, opacity: 0.6 }} />

              {/* Every row: label on the left, a field of the same width on the right. */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>Name</Typography>
                <Box sx={{ width: SELLER_FIELD_WIDTH, flexShrink: 0 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Adam Tan"
                    value={seller.name ?? ''}
                    onChange={(e) => updateSeller(idx, { name: e.target.value })}
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', textAlign: 'right', py: 0.9 } }}
                  />
                </Box>
              </Box>

              {visibleSellers.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>Share of proceeds</Typography>
                  <Box sx={{ width: SELLER_FIELD_WIDTH, flexShrink: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={seller.sharePct}
                      onChange={(e) => updateSellerShare(idx, e.target.value)}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', textAlign: 'right', py: 0.9 } }}
                    />
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>CPF Withdrawal</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>With accrued interest — refunded to their OA</Typography>
                </Box>
                <Box sx={{ width: SELLER_FIELD_WIDTH, flexShrink: 0 }}>
                  <CurrencyTextField
                    value={seller.cpfWithdrawal}
                    onChange={(v) => updateSeller(idx, { cpfWithdrawal: Math.max(0, Math.round(v)) })}
                    highlightColor={tabColor}
                  />
                </Box>
              </Box>
            </Box>
          ))}

          {visibleSellers.length > 1 && Math.abs(sellerShareTotal - 100) > 0.01 && (
            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, display: 'block', mb: 1 }}>
              Shares add up to {Number(sellerShareTotal.toFixed(2))}% — they must total 100%.
            </Typography>
          )}

          {!isSimple && inputs.sellers.length < 2 && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addSeller}
              sx={{ py: 1, borderStyle: 'dashed', borderWidth: 2, borderColor: tabColor, color: tabColor, fontWeight: 700, '&:hover': { borderStyle: 'dashed', borderWidth: 2 } }}
            >
              Add Seller
            </Button>
          )}
        </Box>
      </Card>

      {/* ── EXPENSES ── */}
      <Card data-tutorial="expenses-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Expenses" step={3} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          {propertyTab === 'hdb' && (
            <SliderMoneyInput
              label="Resale Application"
              value={inputs.resaleApplication}
              onChange={(v) => updateInputs('resaleApplication', v)}
              max={500}
              step={10}
              bigStep={20}
              color={tabColor}
              isMobile={isMobile}
            />
          )}
          {/* Agent fee: a plain figure. Tap the amount to change the basis, the
              rate or GST — deliberately undecorated, so it reads as a value. */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
              {agentFeeLabel}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              onClick={openAgentFeeDialog}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openAgentFeeDialog(); }}
              sx={{
                fontSize: '0.95rem',
                flexShrink: 0,
                cursor: 'pointer',
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              {fmt(effectiveAgentFee)}
            </Typography>
          </Box>
          <SliderMoneyInput
            label="Legal Fee"
            value={inputs.legalFee}
            onChange={(v) => updateInputs('legalFee', v)}
            max={10_000}
            step={100}
            bigStep={500}
            color={tabColor}
            isMobile={isMobile}
          />
          {propertyTab === 'hdb' && (
            <SliderMoneyInput
              label="Misc Fee"
              value={inputs.miscFee}
              onChange={(v) => updateInputs('miscFee', v)}
              max={5_000}
              step={50}
              bigStep={100}
              color={tabColor}
              isMobile={isMobile}
            />
          )}
          <SliderMoneyInput
            label="Bank Loan Penalty"
            value={inputs.bankLoanPenalty}
            onChange={(v) => updateInputs('bankLoanPenalty', v)}
            max={100_000}
            step={500}
            bigStep={5_000}
            color={tabColor}
            isMobile={isMobile}
          />
        </Box>
      </Card>

      {/* ── OTHERS (HDB only) ── */}
      {propertyTab === 'hdb' && (
        <Card data-tutorial="others-section" variant="outlined" sx={sectionCardSx}>
          <SectionCardHeader title="Others" step={4} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
          <Box sx={sectionBodySx}>
            <DayNumberInput
              label="Resale Application Days (after Exercise OTP)"
              value={hdbResaleAppDays}
              onChange={setHdbResaleAppDays}
              color={tabColor}
              helperText="Days from Exercise OTP to Resale Application. The Resale Application date updates accordingly."
            />
            <SliderMoneyInput
              label="HDB Upgrading Fee (Outstanding)"
              value={inputs.hdbUpgradingFee}
              onChange={(v) => updateInputs('hdbUpgradingFee', v)}
              max={100_000}
              step={500}
              bigStep={5_000}
              color={tabColor}
              isMobile={isMobile}
            />

            {/* Extension toggle */}
            <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: hdbExtension ? '#D97706' : 'divider', bgcolor: hdbExtension ? 'rgba(217,119,6,0.06)' : 'transparent' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>3-Month Extension</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    2nd property OTP exercised
                  </Typography>
                </Box>
                <Switch
                  checked={hdbExtension}
                  onChange={(e) => setHdbExtension(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#D97706' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#D97706' },
                  }}
                />
              </Box>
              {hdbExtension && (
                <Typography variant="caption" sx={{ color: '#D97706', display: 'block', mt: 0.5, fontWeight: 600 }}>
                  +90 days added to Handover date
                </Typography>
              )}
            </Box>
          </Box>
        </Card>
      )}

      {/* ── SELLER STAMP DUTY (Private only) ── */}
      {propertyTab === 'private' && (
        <Card data-tutorial="ssd-section" variant="outlined" sx={sectionCardSx}>
          <SectionCardHeader
            title="Seller Stamp Duty"
            step={4}
            color={tabColor}
            isDark={isDark}
            cardBorder={cardBorder}
          />
          <Box sx={sectionBodySx}>
            {/* On/off toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>Apply Seller Stamp Duty</Typography>
                  <IconButton
                    size="small"
                    aria-label="View Seller Stamp Duty purchase-date rates"
                    aria-controls={ssdInfoAnchor ? 'seller-stamp-duty-info' : undefined}
                    aria-haspopup="dialog"
                    aria-expanded={Boolean(ssdInfoAnchor)}
                    onClick={(event) => setSsdInfoAnchor(event.currentTarget)}
                    sx={{ width: 36, height: 36, p: 0.5, color: 'text.secondary', touchAction: 'manipulation' }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Popover
                    id="seller-stamp-duty-info"
                    open={Boolean(ssdInfoAnchor)}
                    anchorEl={ssdInfoAnchor}
                    onClose={() => setSsdInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: { maxWidth: 'min(360px, calc(100vw - 32px))' },
                      },
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography variant="caption" component="div" fontWeight={700} sx={{ mb: 0.5 }}>
                        Purchased on or after 4 Jul 2025
                      </Typography>
                      <Typography variant="caption" component="div">
                        Under 1 year: 16% · 1–2 years: 12% · 2–3 years: 8% · 3–4 years: 4%. No SSD from 4 years.
                      </Typography>
                      <Typography variant="caption" component="div" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>
                        Purchased 11 Mar 2017–3 Jul 2025
                      </Typography>
                      <Typography variant="caption" component="div">
                        Under 1 year: 12% · 1–2 years: 8% · 2–3 years: 4%. No SSD from 3 years.
                      </Typography>
                      <Link
                        href={IRAS_SSD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                        underline="always"
                        variant="caption"
                        sx={{ display: 'inline-block', mt: 1, fontWeight: 700 }}
                      >
                        View IRAS SSD rates
                      </Link>
                    </Box>
                  </Popover>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Payable if sold within 3 or 4 years, based on purchase date
                </Typography>
              </Box>
              <Switch
                checked={privateStampDutyEnabled}
                onChange={(e) => setPrivateStampDutyEnabled(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: tabColor },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: tabColor },
                }}
              />
            </Box>

            <Collapse in={privateStampDutyEnabled} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <DateField
                  label="Purchase Date"
                  value={inputs.purchaseDate}
                  onChange={(v) => updateInputs('purchaseDate', v)}
                  info="The date you acquired this property — i.e. when the Option to Purchase was exercised (or the Sale & Purchase Agreement was signed). Used to determine the holding period for Seller's Stamp Duty."
                />
                <DateField
                  label="Sale Date"
                  value={inputs.saleDate}
                  onChange={(v) => updateInputs('saleDate', v)}
                  info="The date this property is sold — i.e. when the buyer exercises the Option to Purchase. Together with the Purchase Date, this determines the SSD holding period."
                />
                <Box sx={{ mb: 0 }}>
                  <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary', fontWeight: 500 }}>Seller Stamp Duty</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    disabled
                    value={ssd.toLocaleString('en-SG')}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.disabledBackground' } }}
                    helperText={inputs.purchaseDate && inputs.saleDate ? (() => {
                      const result = calculateSellerStampDuty(
                        inputs.purchaseDate,
                        inputs.saleDate,
                        inputs.salePrice,
                      );
                      return result
                        ? result.rateLabel.replace(' (', ' — ').replace(')', '')
                        : 'Enter valid dates to calculate SSD';
                    })() : 'Enter both dates to calculate SSD'}
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Card>
      )}
    </Box>
  );

  // ─── Right panel ──────────────────────────────────────────────────────────
  const schedule = propertyTab === 'hdb' ? hdbSchedule : privateSchedule;

  const rightPanel = (
    <Box>
      {/* Sales Proceeds Card */}
      <Card data-tutorial="sales-proceeds" variant="outlined" sx={{ mb: 2, overflow: 'hidden', borderColor: cardBorder }}>
        <Box sx={{ bgcolor: tabBg, px: 2, py: 1.5, textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#fff', letterSpacing: '0.1em' }}>
            SALES PROCEEDS
          </Typography>
        </Box>
        <Box sx={{ px: 2, py: 2.5, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography variant="h4" fontWeight={800}>
            {fmt(Math.max(0, salesProceeds))}
          </Typography>
          {salesProceeds < 0 && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
              Shortfall: {fmt(-salesProceeds)}
            </Typography>
          )}
        </Box>
      </Card>

      {/* Proceeds Breakdown */}
      <Card data-tutorial="proceeds-breakdown" variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          Proceeds Breakdown
        </Typography>
        {[
          { label: 'Sale Price', amount: inputs.salePrice, sign: '+' },
          { label: 'Outstanding Loan', amount: inputs.outstandingLoan, sign: '−' },
          { label: 'CPF Withdrawal (w/ Accrued Interest)', amount: cpfRefundTotal, sign: '−' },
        ].map((row) => (
          <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{row.sign} {row.label}</Typography>
            <Typography variant="caption" fontWeight={600}>{fmt(row.amount)}</Typography>
          </Box>
        ))}

        {/* Total Expenses (clickable to expand breakdown) */}
        <Box
          onClick={() => expenseItems.length > 0 && setShowExpenseBreakdown((v) => !v)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.75,
            cursor: expenseItems.length > 0 ? 'pointer' : 'default',
            borderRadius: 0.5,
            px: 0.5,
            mx: -0.5,
            '&:hover': expenseItems.length > 0
              ? { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }
              : undefined,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>− Total Expenses</Typography>
            {expenseItems.length > 0 && (
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  transition: 'transform 0.2s',
                  transform: showExpenseBreakdown ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            )}
          </Box>
          <Typography variant="caption" fontWeight={600}>{fmt(totalExpenses)}</Typography>
        </Box>
        <Collapse in={showExpenseBreakdown} timeout="auto" unmountOnExit>
          <Box
            sx={{
              ml: 1.5,
              pl: 1.5,
              borderLeft: '2px solid',
              borderColor: 'divider',
              mb: 0.75,
            }}
          >
            {expenseItems.map((item) => (
              <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {item.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {fmt(item.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>

        {ssd > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>− Seller Stamp Duty</Typography>
            <Typography variant="caption" fontWeight={600}>{fmt(ssd)}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: tabColor }}>Net Sales Proceeds</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: salesProceeds < 0 ? 'error.main' : tabColor }}>
            {fmt(salesProceeds)}
          </Typography>
        </Box>
      </Card>

      {/* Proceeds Split — only meaningful once the sale has more than one owner.
          CPF is refunded to each owner's own OA first; the cash that remains is what
          the ownership shares divide. The refund is the seller's own money coming
          back, so it is shown as something received, not as a cost. */}
      {effectiveSellers.length > 1 && (
        <Card data-tutorial="proceeds-split" variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            Proceeds Split
          </Typography>

          {/* The figure the shares divide — the same Net Sales Proceeds as above. */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Net cash proceeds to divide</Typography>
            <Typography variant="caption" fontWeight={700} sx={{ color: salesProceeds < 0 ? 'error.main' : 'text.primary' }}>
              {fmt(Math.round(salesProceeds))}
            </Typography>
          </Box>

          {sellerSplit.map((seller, idx) => (
            <Box key={idx} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" fontWeight={700}>{seller.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {Number(seller.sharePct.toFixed(2))}%
                </Typography>
              </Box>
              <Box sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                  <Typography variant="caption" fontWeight={700}>
                    Cash to {seller.name} ({Number(seller.sharePct.toFixed(2))}% of {fmt(Math.round(salesProceeds))})
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: seller.cash < 0 ? 'error.main' : tabColor }}>
                    {fmt(seller.cash)}
                  </Typography>
                </Box>
                {seller.cpfRefund > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>CPF refund (back to their OA)</Typography>
                    <Typography variant="caption">{fmt(seller.cpfRefund)}</Typography>
                  </Box>
                )}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5, pt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    = Total to {seller.name} (cash + CPF)
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>{fmt(seller.total)}</Typography>
                </Box>
              </Box>
            </Box>
          ))}

          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: tabColor }}>Total cash to sellers</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: salesProceeds < 0 ? 'error.main' : tabColor }}>
              {fmt(salesProceeds)}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Sales Schedule */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>SALES SCHEDULE</Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
        </Box>

        <Box data-tutorial="show-dates" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <TimelineDatesToggle value={showTimeline} onChange={setShowTimeline} />
        </Box>

        <Box data-tutorial="sales-schedule" sx={{ position: 'relative' }}>
          {/* Vertical connector line */}
          <Box sx={{
            position: 'absolute', left: 13, top: 28, bottom: 28,
            width: 2, bgcolor: isDark ? 'rgba(217,119,6,0.3)' : '#fde68a', zIndex: 0,
          }} />
          {schedule.map((stage) => (
            <ScheduleStageCard
              key={stage.num}
              stage={stage}
              cardBorder={cardBorder}
              showDate={showTimeline}
              onDateChange={propertyTab === 'hdb' ? updateHdbDate : updatePrivateDate}
              onPctChange={stage.num === 1 && propertyTab === 'private'
                ? setOtpPercent
                : stage.num === 2 && propertyTab === 'private'
                  ? setExercisePercent
                  : undefined}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
          Super Selling Calculator
        </Typography>
        <TutorialLaunchButton controller={tutorial} label="the Selling Calculator" />
        <WhatsNewLaunchButton controller={whatsNew} label="the Selling Calculator" />
      </Box>

      {/* Load Example / History + Actions */}
      <Box data-tutorial="load-example">
        <HistoryToolbar
          analyticsCalculator="selling_calculator"
          onHistoryLoad={() => { /* state already restored via setter */ }}
          onSaveToProfile={saveInputsToProfile}
          onBuildNewProfiles={buildNewProfilesFromInputs}
          onBeforeProfileCreate={beginProfileSaveBack}
          onAfterProfileCreate={endProfileSaveBack}
          extraButtons={
            <>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => setClearDialogOpen(true)}
              >
                Clear
              </Button>
              <ExportPdfButton onExport={handleExportPdf} />
            </>
          }
        />
      </Box>

      {/* Tabs + display controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Tabs
          data-tutorial="property-tabs"
          value={propertyTab}
          onChange={(_, v) => {
            setPropertyTab(v);
            logCalculatorInteraction('selling_calculator', 'tab_switch', { tab: v });
          }}
          sx={{
            '& .MuiTab-root': { fontWeight: 700, fontSize: '0.9rem' },
            '& .MuiTabs-indicator': { bgcolor: tabBg },
            '& .Mui-selected': { color: `${tabBg} !important` },
          }}
        >
          <Tab value="private" label="Private" />
          <Tab value="hdb" label="HDB" />
        </Tabs>
        <Box data-tutorial="display-controls" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Text size — smaller / larger (shared across calculators) */}
          <TextSizeControl />
          {!isMobile && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={splitScroll ? 'split' : 'single'}
              onChange={(_, v) => { if (v !== null) setSplitScroll(v === 'split'); }}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.25, py: 0.5 },
                '& .Mui-selected': { color: `${tabColor} !important`, bgcolor: isDark ? 'rgba(255,255,255,0.10) !important' : 'rgba(30,58,95,0.08) !important' },
              }}
            >
              <Tooltip title="Page scrolls as one column">
                <ToggleButton value="single">1 Scroll</ToggleButton>
              </Tooltip>
              <Tooltip title="Inputs and results scroll independently">
                <ToggleButton value="split">2 Scroll</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* ── Agent Service Fee editor ── */}
      <Dialog open={agentFeeDialogOpen} onClose={() => setAgentFeeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Agent Service Fee</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Charge as</Typography>
          <RadioGroup
            row
            value={agentFeeDraftMode}
            onChange={(e) => setAgentFeeDraftMode(e.target.value as 'pct' | 'amount')}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="pct" control={<Radio size="small" />} label={<Typography variant="body2">Percentage</Typography>} />
            <FormControlLabel value="amount" control={<Radio size="small" />} label={<Typography variant="body2">Fixed amount</Typography>} />
          </RadioGroup>

          {agentFeeDraftMode === 'pct' ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Percentage of sale price"
              value={agentFeeDraftPct}
              onChange={(e) => setAgentFeeDraftPct(e.target.value.replace(/[^0-9.]/g, ''))}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          ) : (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Fee amount (before GST)"
              value={agentFeeDraftAmount ? agentFeeDraftAmount.toLocaleString('en-SG') : ''}
              onChange={(e) => setAgentFeeDraftAmount(Number(e.target.value.replace(/[^0-9]/g, '')) || 0)}
              inputProps={{ inputMode: 'numeric' }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          )}

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={agentFeeDraftGst}
                onChange={(e) => setAgentFeeDraftGst(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Include GST ({Math.round((AGENT_FEE_GST - 1) * 100)}%)</Typography>}
          />

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {agentFeeDraftMode === 'pct'
                ? `Fee (${Number.parseFloat(agentFeeDraftPct) || 0}% of ${fmt(inputs.salePrice)})`
                : 'Fee'}
            </Typography>
            <Typography variant="body2" fontWeight={600}>{fmt(agentFeeDraftBase)}</Typography>
          </Box>
          {agentFeeDraftGst && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">+ GST {Math.round((AGENT_FEE_GST - 1) * 100)}%</Typography>
              <Typography variant="body2" fontWeight={600}>{fmt(agentFeeDraftGstAmount)}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" fontWeight={700}>Total charged</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: tabColor }}>
              {fmt(agentFeeDraftBase + agentFeeDraftGstAmount)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setAgentFeeDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={commitAgentFee} size="small" variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>

      {/* ── Clear Calculator Confirmation Dialog ── */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartAltIcon color="error" />
          Reset Selling Calculator?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will reset all Selling Calculator values to their original defaults and clear the selected example or history entry.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your saved history entries will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setClearDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={clearCalculator} size="small" variant="contained" color="error">
            Clear &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tutorial */}
      <CalculatorTutorial
        controller={tutorial}
        title="Selling Calculator Tutorial"
        manualName="Super Selling Calculator"
        steps={SELLING_TUTORIAL_STEPS}
        manualExtraSections={SELLING_MANUAL_SECTIONS}
      />

      {/* ── What's New ── */}
      <WhatsNewDialog controller={whatsNew} />

      {/* 2-column layout — the same one the Buying Calculator uses. Below `md` the two
          grid items stack in source order, so the inputs come first and the results
          follow them, rather than the agent landing on a result for figures they have
          not entered yet. `columnScrollSx` is already undefined on mobile, so neither
          column takes its own scroll region there. */}
      <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
        <Grid item xs={12} md={5}>
          <Box sx={columnScrollSx}>{leftPanel}</Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={columnScrollSx}>{rightPanel}</Box>
        </Grid>
      </Grid>
    </Box>
  );
}
